import { computed, type ComputedRef } from 'vue';
import { useTheme } from 'vuetify';
import Intercom, {
  shutdown as shutdownIntercom
} from '@intercom/messenger-js-sdk';
import UserProfileClass from '@/services/userProfile/UserProfileClass';
import {
  clearIntercomUser,
  updateIntercomUserData,
  syncIntercomUser,
  isIntercomEnabled
} from '@/utils/intercomUtils';

const { VITE_INTERCOM_APP_ID } = import.meta.env;

type UserProfile = InstanceType<typeof UserProfileClass>;

type UpdateIntercomUserParams = {
  userProfile: UserProfile;
  hasSingleContext: boolean;
};

type UseIntercomReturn = {
  intercomColor: ComputedRef<{ action: string; background: string }>;
  intercomThemeMode: ComputedRef<'dark' | 'light'>;
  initIntercom: () => void;
  updateIntercomUser: (params: UpdateIntercomUserParams) => void;
  updateIntercomTheme: () => void;
  resetIntercom: () => void;
  clearIntercomUser: () => void;
};

// Track if Intercom has been initialized globally to prevent re-initialization across composable invocations
let isIntercomInitialized = false;

/**
 * Composable to manage Intercom configuration and initialization
 *
 * Handles:
 * - SDK initialization
 * - Theme color syncing with Vuetify
 * - User profile updates
 *
 * @returns {Object} Intercom configuration and functions
 */
export const useIntercom = (): UseIntercomReturn => {
  const vTheme = useTheme();

  // Theme-aware colors
  const intercomColor = computed(() => ({
    action:
      (vTheme.current.value.dark
        ? vTheme.current.value.colors['primary-700']
        : vTheme.current.value.colors.primary) || '#1A31D5',
    background: vTheme.current.value.colors.primary || '#1A31D5'
  }));

  const intercomThemeMode = computed(() =>
    vTheme.current.value.dark ? 'dark' : 'light'
  );

  /**
   * Initialize the Intercom SDK with current settings
   */
  const initIntercom = () => {
    if (!isIntercomEnabled() || isIntercomInitialized) return;

    Intercom({
      app_id: VITE_INTERCOM_APP_ID,
      action_color: intercomColor.value.action,
      background_color: intercomColor.value.background,
      theme_mode: intercomThemeMode.value
    });

    isIntercomInitialized = true;
  };

  /**
   * Update Intercom with user profile information
   * Delegates to utility function for production-only syncing with version tracking
   * @param params.userProfile - User profile object with email, firstname, lastname, permissions, tenants
   * @param params.hasSingleContext - Whether user has a single context
   */
  const updateIntercomUser = ({
    userProfile,
    hasSingleContext
  }: UpdateIntercomUserParams) => {
    syncIntercomUser(userProfile, { hasSingleContext });
  };

  /**
   * Update theme colors in Intercom
   */
  const updateIntercomTheme = () => {
    updateIntercomUserData({
      action_color: intercomColor.value.action,
      background_color: intercomColor.value.background,
      theme_mode: intercomThemeMode.value
    });
  };

  /**
   * Reset Intercom (shutdown and reinitialize)
   * Useful when auth state changes or permissions are updated
   */
  const resetIntercom = () => {
    if (isIntercomInitialized) {
      shutdownIntercom();
      isIntercomInitialized = false;
    }

    // Always reinitialize since Intercom is always enabled
    initIntercom();
  };

  // Auto-initialize Intercom on first use.
  // Check it's not already initialized to avoid redundant calls
  if (!isIntercomInitialized) {
    initIntercom();
  }

  return {
    intercomColor,
    intercomThemeMode,
    initIntercom,
    updateIntercomUser,
    updateIntercomTheme,
    resetIntercom,
    clearIntercomUser
  };
};
